import React, {useEffect, useState} from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import './InAppBrowserOverlay.css';

/**
 * InAppBrowserOverlay (task #26)
 *
 * Full-viewport overlay that renders an external link inside the Flow app
 * (an <iframe>) instead of blowing away the chat context in a brand new
 * browser tab. Supports a simple back-only navigation history and always
 * offers an "open in a new tab" escape hatch, since cross-origin iframes
 * can silently fail to embed (X-Frame-Options / CSP frame-ancestors) with
 * no reliable way for the parent page to detect it.
 *
 * State (isOpen / history / currentIndex) is owned by the caller (see
 * `MainApp` in App.jsx, `inAppBrowser` state) — this component is a
 * "dumb" renderer driven by props, mirroring how `TenantSwitchOverlay` is
 * used elsewhere in this app.
 */
const truncateUrl = (url, max = 70) => {
  if (!url) {
    return '';
  }
  if (url.length <= max) {
    return url;
  }
  return `${url.slice(0, max - 1)}…`;
};

const InAppBrowserOverlay = ({url, canGoBack, onBack, onClose}) => {
  const [isLoading, setIsLoading] = useState(true);

  // Any time the displayed URL changes (new link opened, or "back" pressed),
  // show the loading state again until the new iframe document fires onLoad.
  useEffect(() => {
    setIsLoading(true);
  }, [url]);

  if (!url) {
    return null;
  }

  const handleOpenNewTab = () => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return ReactDOM.createPortal(
    <div className="in-app-browser-overlay">
      <div className="in-app-browser-toolbar">
        <button
          aria-label="Back"
          className="in-app-browser-btn in-app-browser-back"
          disabled={!canGoBack}
          onClick={onBack}
          title="Back"
          type="button"
        >
          {'←'}
        </button>

        <div className="in-app-browser-address" title={url}>
          {truncateUrl(url)}
        </div>

        <button
          aria-label="Open in new tab"
          className="in-app-browser-btn"
          onClick={handleOpenNewTab}
          title="Open in new tab"
          type="button"
        >
          {'Open in new tab ↗'}
        </button>

        <button
          aria-label="Close"
          className="in-app-browser-btn in-app-browser-close"
          onClick={onClose}
          title="Close"
          type="button"
        >
          {'×'}
        </button>
      </div>

      {/* Permanently-visible escape hatch: iframes can be blocked from
          embedding by the target site with no reliable way to detect it
          from here, so we don't try to be clever — just always offer this. */}
      <div className="in-app-browser-hint">
        {'Trouble loading this page? '}
        <button
          className="in-app-browser-hint-link"
          onClick={handleOpenNewTab}
          type="button"
        >
          {'Open in a new tab instead →'}
        </button>
      </div>

      <div className="in-app-browser-frame-wrapper">
        {isLoading && (
          <div className="in-app-browser-loading">
            <div className="in-app-browser-spinner" />
          </div>
        )}
        <iframe
          className="in-app-browser-iframe"
          // Remount on every URL change (new link, or "back") so the iframe
          // reliably navigates instead of relying on imperatively setting
          // `.src` on a ref.
          key={url}
          onLoad={() => setIsLoading(false)}
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          src={url}
          title="In-app browser"
        />
      </div>
    </div>,
    document.body
  );
};

InAppBrowserOverlay.propTypes = {
  canGoBack: PropTypes.bool,
  onBack: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  url: PropTypes.string,
};

InAppBrowserOverlay.defaultProps = {
  canGoBack: false,
  url: null,
};

export default InAppBrowserOverlay;
